import React, { useCallback, useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Avatar, AvatarFallback } from "@/components/ui/avatar";
import { toast } from "sonner";
import {
  ArrowLeft,
  Edit,
  ToggleLeft,
  ToggleRight,
  MapPin,
  Car,
  Clock,
  Calendar,
  TrendingUp,
  DollarSign,
  Zap,
  Activity,
  User,
  Settings,
  Info,
  CheckCircle,
  XCircle,
  AlertCircle,
} from "lucide-react";
import { useSurgePricing, useToggleSurgePricingStatus } from "@/api-services/hooks/useSurgePricing";
import { useAllVehicleCategories } from "@/api-services/hooks/useVehicleCategory";
import { SurgePricing } from "@/api-services/hooks/surgePricingTypes";
import { formatGlobalCurrency } from "@/api-services/hooks/useSettings";

const ViewSurgePricingPage = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const surgePricingId = parseInt(id || "0");

  // State for managing toggle confirmation
  const [isToggling, setIsToggling] = useState(false);

  // Fetch vehicle categories for name lookup
  const { data: vehicleCategoriesData } = useAllVehicleCategories() as { data: { vehicleCategoryId: number; vehicleCategoryName: string }[] | undefined };

  // Fetch surge pricing details
  const { data: surgePricing, loading, error, refetch } = useSurgePricing(
    surgePricingId,
    {
      enabled: !!surgePricingId && surgePricingId > 0,
      onError: (err) => {
        toast.error(`Failed to load surge pricing details: ${err.message}`);
      },
    }
  );

  // Toggle surge pricing status hook
  const { mutate: toggleStatus } = useToggleSurgePricingStatus({
    onSuccess: (data, surgePricingId) => {
      setIsToggling(false);
      toast.success("Surge pricing status updated successfully!");
      refetch(); // Refresh the data
    },
    onError: (error, surgePricingId) => {
      setIsToggling(false);
      toast.error(`Failed to update surge pricing status: ${error.message}`);
    },
  });

  // Format time from LocalTime object or string
  const formatTime = useCallback((time: { hour: number; minute: number } | string) => {
    if (typeof time === 'string') {
      const [hours, minutes] = time.split(':');
      return `${hours}:${minutes}`;
    } else if (time && typeof time === 'object' && 'hour' in time && 'minute' in time) {
      const hour = time.hour.toString().padStart(2, "0");
      const minute = time.minute.toString().padStart(2, "0");
      return `${hour}:${minute}`;
    }
    return '00:00';
  }, []);

  // Format currency display
  const formatCurrency = useCallback((amount: number) => {
    return new Intl.NumberFormat("en-US", {
      style: "currency",
      currency: "USD",
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    }).format(amount);
  }, []);

  // Get day display name
  const getDayDisplayName = useCallback((day: string) => {
    const days: Record<string, string> = {
      MONDAY: "Monday",
      TUESDAY: "Tuesday",
      WEDNESDAY: "Wednesday",
      THURSDAY: "Thursday",
      FRIDAY: "Friday",
      SATURDAY: "Saturday",
      SUNDAY: "Sunday",
    };
    return days[day] || day;
  }, []);

  // Format date
  const formatDate = useCallback((dateString?: string) => {
    if (!dateString) return "N/A";
    return new Date(dateString).toLocaleString();
  }, []);

  // Handle status toggle
  const handleToggleStatus = () => {
    if (!surgePricing) return;

    setIsToggling(true);
    toggleStatus(surgePricing.surgeRuleId);
  };

  // Handle navigation
  const handleEdit = () => {
    navigate(`/admin/surge-pricing/edit/${surgePricingId}`);
  };

  const handleBack = () => {
    navigate("/admin/surge-pricing");
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <div className="text-center">
          <div className="w-8 h-8 border-4 border-primary border-t-transparent rounded-full animate-spin mx-auto mb-4" />
          <p className="text-muted-foreground">Loading surge pricing details...</p>
        </div>
      </div>
    );
  }

  if (error || !surgePricing) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <Card className="max-w-md">
          <CardContent className="p-6 text-center">
            <AlertCircle className="w-12 h-12 text-destructive mx-auto mb-4" />
            <h3 className="text-lg font-semibold mb-2">
              Error Loading Surge Pricing
            </h3>
            <p className="text-muted-foreground mb-4">
              {error?.message || "Surge pricing not found"}
            </p>
            <div className="flex gap-2 justify-center">
              <Button variant="outline" onClick={() => refetch()}>
                Try Again
              </Button>
              <Button onClick={handleBack}>Back to List</Button>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="space-y-6 animate-fade-in">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-4">
          <Button variant="outline" size="sm" onClick={handleBack}>
            <ArrowLeft className="w-4 h-4 mr-2" />
            Back to Surge Pricing
          </Button>
          <div>
            <h1 className="text-3xl font-bold text-foreground">
              Surge Pricing Details
            </h1>
            <p className="text-muted-foreground mt-1">
              View detailed information about this surge pricing rule
            </p>
          </div>
        </div>
        <div className="flex gap-2">
          <Button
            variant="outline"
            onClick={handleToggleStatus}
            disabled={isToggling}
          >
            {surgePricing.isActive ? (
              <>
                <ToggleLeft className="w-4 h-4 mr-2" />
                Deactivate
              </>
            ) : (
              <>
                <ToggleRight className="w-4 h-4 mr-2" />
                Activate
              </>
            )}
          </Button>
          <Button onClick={handleEdit}>
            <Edit className="w-4 h-4 mr-2" />
            Edit Surge Rule
          </Button>
        </div>
      </div>

      {/* Main Content Grid */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Left Column - Main Information */}
        <div className="lg:col-span-2 space-y-6">
          {/* Basic Information Card */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Zap className="w-5 h-5" />
                Surge Pricing Rule Information
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-6">
              {/* Location and Vehicle */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <label className="text-sm font-medium text-muted-foreground flex items-center gap-2">
                    <MapPin className="w-4 h-4" />
                    City
                  </label>
                  <div className="p-3 bg-muted/30 rounded-lg">
                    <p className="font-semibold">
                      {surgePricing.city?.cityName || "Unknown City"}
                    </p>
                  </div>
                </div>
                <div className="space-y-2">
                  <label className="text-sm font-medium text-muted-foreground flex items-center gap-2">
                    <Car className="w-4 h-4" />
                    Vehicle Category
                  </label>
                  <div className="p-3 bg-muted/30 rounded-lg">
                    <p className="font-semibold">
                      {vehicleCategoriesData?.find(cat => cat.vehicleCategoryId === surgePricing.vehicleCategoryId)?.vehicleCategoryName || "Unknown Vehicle"}
                    </p>
                  </div>
                </div>
              </div>

              {/* Schedule */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <label className="text-sm font-medium text-muted-foreground flex items-center gap-2">
                    <Calendar className="w-4 h-4" />
                    Day of Week
                  </label>
                  <div className="p-3 bg-muted/30 rounded-lg">
                    <p className="font-semibold">
                      {getDayDisplayName(surgePricing.dayOfWeek)}
                    </p>
                  </div>
                </div>
                <div className="space-y-2">
                  <label className="text-sm font-medium text-muted-foreground flex items-center gap-2">
                    <Clock className="w-4 h-4" />
                    Time Range
                  </label>
                  <div className="p-3 bg-muted/30 rounded-lg">
                    <p className="font-semibold">
                      {formatTime(surgePricing.startTime)} - {formatTime(surgePricing.endTime)}
                    </p>
                  </div>
                </div>
              </div>

              {/* Pricing Details */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <label className="text-sm font-medium text-muted-foreground flex items-center gap-2">
                    <TrendingUp className="w-4 h-4" />
                    Surge Multiplier
                  </label>
                  <div className="p-3 bg-orange-50 border border-orange-200 rounded-lg">
                    <p className="font-bold text-orange-600 text-lg">
                      {surgePricing.surgeMultiplier.toFixed(2)}x
                    </p>
                  </div>
                </div>
                <div className="space-y-2">
                  <label className="text-sm font-medium text-muted-foreground flex items-center gap-2">
                    <DollarSign className="w-4 h-4" />
                    Maximum Surge Cap
                  </label>
                  <div className="p-3 bg-red-50 border border-red-200 rounded-lg">
                    <p className="font-bold text-red-600 text-lg">
                      {formatGlobalCurrency(surgePricing.maxSurgeCap)}
                    </p>
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* System Information Card */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Settings className="w-5 h-5" />
                System Information
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <label className="text-sm font-medium text-muted-foreground flex items-center gap-2">
                    <User className="w-4 h-4" />
                    Created By
                  </label>
                  <div className="p-3 bg-muted/30 rounded-lg">
                    <p className="font-medium">
                      {surgePricing.createdBy || "Unknown"}
                    </p>
                  </div>
                </div>
                <div className="space-y-2">
                  <label className="text-sm font-medium text-muted-foreground flex items-center gap-2">
                    <Info className="w-4 h-4" />
                    Status
                  </label>
                  <div className="p-3 bg-muted/30 rounded-lg">
                    <Badge
                      className={
                        surgePricing.isActive
                          ? "bg-success text-success-foreground"
                          : "bg-muted text-muted-foreground"
                      }
                    >
                      {surgePricing.isActive ? (
                        <>
                          <CheckCircle className="w-3 h-3 mr-1" />
                          Active
                        </>
                      ) : (
                        <>
                          <XCircle className="w-3 h-3 mr-1" />
                          Inactive
                        </>
                      )}
                    </Badge>
                  </div>
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <label className="text-sm font-medium text-muted-foreground">
                    Created Date
                  </label>
                  <div className="p-3 bg-muted/30 rounded-lg">
                    <p className="text-sm">
                      {formatDate(surgePricing.createdDate)}
                    </p>
                  </div>
                </div>
                <div className="space-y-2">
                  <label className="text-sm font-medium text-muted-foreground">
                    Last Modified
                  </label>
                  <div className="p-3 bg-muted/30 rounded-lg">
                    <p className="text-sm">
                      {formatDate(surgePricing.modifiedDate)}
                    </p>
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Right Column - Status & Actions */}
        <div className="space-y-6">
          {/* Status Card */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Activity className="w-5 h-5" />
                Rule Status
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="text-center">
                <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-gradient-to-br from-blue-500 to-purple-600 flex items-center justify-center">
                  <Zap className="w-8 h-8 text-white" />
                </div>
                <Badge
                  variant={surgePricing.isActive ? "default" : "secondary"}
                  className={
                    surgePricing.isActive
                      ? "bg-success text-success-foreground text-sm px-3 py-1"
                      : "bg-muted text-muted-foreground text-sm px-3 py-1"
                  }
                >
                  {surgePricing.isActive ? "Active" : "Inactive"}
                </Badge>
              </div>

              <div className="space-y-2 text-sm">
                <div className="flex justify-between">
                  <span className="text-muted-foreground">Rule ID:</span>
                  <span className="font-mono">#{surgePricing.surgeRuleId}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-muted-foreground">Deleted:</span>
                  <span>{surgePricing.isDeletedValue ? "Yes" : "No"}</span>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Quick Actions Card */}
          <Card>
            <CardHeader>
              <CardTitle>Quick Actions</CardTitle>
            </CardHeader>
            <CardContent className="space-y-3">
              <Button
                onClick={handleEdit}
                className="w-full"
              >
                <Edit className="w-4 h-4 mr-2" />
                Edit Surge Rule
              </Button>
              <Button
                variant="outline"
                onClick={handleToggleStatus}
                disabled={isToggling}
                className="w-full"
              >
                {surgePricing.isActive ? (
                  <>
                    <ToggleLeft className="w-4 h-4 mr-2" />
                    Deactivate Rule
                  </>
                ) : (
                  <>
                    <ToggleRight className="w-4 h-4 mr-2" />
                    Activate Rule
                  </>
                )}
              </Button>
              <Button
                variant="outline"
                onClick={() => navigate("/admin/surge-pricing")}
                className="w-full"
              >
                <ArrowLeft className="w-4 h-4 mr-2" />
                Back to List
              </Button>
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
};

export default ViewSurgePricingPage;